/**
 * BytePlus ModelArk — Seedream 5.0 (image) + Seedance 2.0 / 2.0 Fast (video).
 * Region: ap-southeast or eu-west. We default to ap-southeast and let the user
 * override via settings.
 *
 * NOTE: model IDs (seedream-5-0-260128, dreamina-seedance-2-0-260128,
 * dreamina-seedance-2-0-fast-260128) and the task-based async API shape were
 * supplied by the user. The request/response wrappers below match the public
 * ModelArk OpenAPI conventions (/contents/generations/tasks); verify against
 * the current docs before launch — see docs/KNOWN_UNKNOWNS.md.
 */
import type { GenerateRequest, GenerateResult, ProviderAdapter, ModelDescriptor } from '../router/types';
import { proxy, ok, queued, fail } from './common';

const REGIONS = {
  ap: 'https://ark.ap-southeast.bytepluses.com/api/v3',
  eu: 'https://ark.eu-west.bytepluses.com/api/v3'
} as const;
type Region = keyof typeof REGIONS;

function base(region: Region = 'ap') { return REGIONS[region]; }

export const BYTEPLUS_MODELS: ModelDescriptor[] = [
  {
    id: 'seedream-5-0-260128',
    label: 'Seedream 5.0',
    mode: 'image',
    caps: { multiRef: 6, edit: true, aspects: ['1:1','3:4','4:3','9:16','16:9','21:9'], maxRes: '2k' }
  },
  {
    id: 'dreamina-seedance-2-0-260128',
    label: 'Seedance 2.0',
    mode: 'video',
    caps: {
      multiRef: 6, startEndFrame: true, audio: true, edit: true,
      bgRemove: true, transparentBg: true, maxDurationSec: 12,
      aspects: ['16:9','9:16','1:1','21:9','4:3','3:4'], maxRes: '1080p'
    }
  },
  {
    id: 'dreamina-seedance-2-0-fast-260128',
    label: 'Seedance 2.0 Fast',
    mode: 'video',
    caps: {
      multiRef: 4, startEndFrame: true, audio: true, edit: true,
      bgRemove: true, maxDurationSec: 8,
      aspects: ['16:9','9:16','1:1'], maxRes: '720p'
    }
  }
];

interface SeedanceInput {
  type: 'image' | 'video' | 'audio' | 'text';
  url?: string;
  text?: string;
  role?: string;
}

function buildSeedanceInputs(req: GenerateRequest): SeedanceInput[] {
  const inputs: SeedanceInput[] = [];
  if (req.prompt) inputs.push({ type: 'text', text: req.prompt });
  for (const r of req.refs ?? []) inputs.push({ type: 'image', url: r.url, role: r.role ?? 'subject' });
  for (const v of req.refVideos ?? []) inputs.push({ type: 'video', url: v.url, role: v.role ?? 'edit-source' });
  for (const a of req.refAudio ?? []) inputs.push({ type: 'audio', url: a.url, role: a.role ?? 'voice' });
  if (req.startFrame) inputs.push({ type: 'image', url: req.startFrame.url, role: 'start-frame' });
  if (req.endFrame) inputs.push({ type: 'image', url: req.endFrame.url, role: 'end-frame' });
  return inputs;
}

async function seedreamGenerate(req: GenerateRequest, apiKey: string, region: Region = 'ap'): Promise<GenerateResult> {
  const endpoint = `${base(region)}/images/generations`;
  const body = {
    model: 'seedream-5-0-260128',
    prompt: req.prompt,
    negative_prompt: req.negativePrompt,
    size: req.aspect ?? '1:1',
    image: (req.refs ?? []).map(r => r.url),
    response_format: 'b64_json',
    n: 1,
    seed: req.seed
  };
  const res = await proxy<any>({ provider: 'byteplus', endpoint, body, apiKey, authStyle: 'bearer' });
  const data = res.data ?? [];
  return ok(data.map((d: any) => ({
    url: d.url ?? `data:image/png;base64,${d.b64_json}`,
    mime: 'image/png'
  })));
}

async function seedanceCreate(modelId: string, req: GenerateRequest, apiKey: string, region: Region = 'ap'): Promise<GenerateResult> {
  const endpoint = `${base(region)}/contents/generations/tasks`;
  const body = {
    model: modelId,
    content: buildSeedanceInputs(req),
    parameters: {
      aspect_ratio: req.aspect ?? '16:9',
      duration: req.durationSec ?? 6,
      fps: req.fps ?? 24,
      resolution: req.resolution ?? '1080p',
      generate_audio: true,
      negative_prompt: req.negativePrompt,
      seed: req.seed,
      ...(req.providerOpts ?? {})
    }
  };
  const res = await proxy<any>({ provider: 'byteplus', endpoint, body, apiKey, authStyle: 'bearer' });
  const taskId = res.id ?? res.task_id ?? res.data?.id;
  if (!taskId) return fail('No task id returned', res);
  return queued(`${region}:${taskId}`, res);
}

async function seedancePoll(taskId: string, apiKey: string): Promise<GenerateResult> {
  const [region, id] = taskId.includes(':') ? taskId.split(':') as [Region, string] : ['ap' as Region, taskId];
  const endpoint = `${base(region)}/contents/generations/tasks/${id}`;
  const res = await proxy<any>({ provider: 'byteplus', endpoint, method: 'GET', apiKey, authStyle: 'bearer' });
  const status = (res.status ?? res.data?.status ?? '').toLowerCase();
  if (status === 'succeeded' || status === 'completed') {
    const out = res.data?.outputs ?? res.outputs ?? [];
    return ok(out.map((o: any) => ({
      url: o.url ?? o.video_url,
      mime: o.mime ?? 'video/mp4',
      width: o.width,
      height: o.height,
      durationMs: o.duration ? Math.round(o.duration * 1000) : undefined
    })));
  }
  if (status === 'failed' || status === 'cancelled') {
    return fail(res.error?.message ?? 'BytePlus task failed', res);
  }
  return { status: 'running', taskId, raw: res };
}

export const byteplus: ProviderAdapter = {
  id: 'byteplus',
  models: BYTEPLUS_MODELS,
  async generate(modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
    if (modelId.startsWith('seedream')) return seedreamGenerate(req, apiKey);
    if (modelId.startsWith('dreamina-seedance')) return seedanceCreate(modelId, req, apiKey);
    return fail(`Unknown BytePlus model: ${modelId}`);
  },
  poll: seedancePoll
};